import cx from "classnames";
import { type ForwardedRef, useEffect, useRef, useState, useImperativeHandle } from "react";

import { type DisabledData, type ExportHandleRef, ExportModalMode } from "../ExportModal";
import { Exporter } from "../../Exporter";
import { Flex, Tabs } from "@theorgchartdev/storybook";
import { type FormController, useFormController } from "@/hooks/useFormController";
import {
  getDefaultExportName,
  hasInvalidFilenameChars,
  readStoredExportSettings,
  storeExportSettings
} from "../ExportModal.utils";
import { handlePreviewUpdate } from "../PreviewCanvas/PreviewCanvas.utils";
import { ImageExportGeneral } from "./ImageExportGeneral";
import { PreviewCanvas } from "../PreviewCanvas";
import { useStatusController } from "@/hooks/useStatusController";
import { enumFillMode } from "@coreapp/constants";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { appstr } from "@coreapp/utils/appstr";

type Props = {
  mode: ExportModalMode;
  handleRef: ForwardedRef<ExportHandleRef>;
  setDisabled: (key: string, value: DisabledData) => void;
};

export type FieldData = {
  name: string;
  cropToChart: boolean;
  transparentBackground: boolean;
  whiteBackground: boolean;
  highQuality: boolean;
};

export type TabProps = {
  formController: FormController<FieldData>;
  mode: ExportModalMode;
};

export const ImageExportContent = (props: Props) => {
  const [currentTab, setCurrentTab] = useState(0);

  const exportStartedRef = useRef(false);
  const statusController = useStatusController();
  const backgroundRef = useRef({ color: "#FFFFFF", fillMode: enumFillMode.solidFill });
  const sourceDocumentRef = useRef<QXDoc | null>(null);
  const mockDocumentRef = useRef<QXDoc | null>(null);

  const formController = useFormController<FieldData>(
    () => ({
      name: getDefaultExportName(),
      ...readStoredExportSettings(
        {
          cropToChart: false,
          transparentBackground: false,
          whiteBackground: true,
          highQuality: true
        },
        "image"
      )
    }),
    {
      onChange: (nextFieldData: FieldData, updatedKey: keyof FieldData) => {
        if (updatedKey === "name") {
          const isInvalid = hasInvalidFilenameChars(nextFieldData.name);
          props.setDisabled("invalidFilename", {
            isDisabled: isInvalid,
            tooltip: isInvalid ? appstr("exportFilenameInvalidChars") : undefined
          });
        }

        storeExportSettings("image", updatedKey, nextFieldData[updatedKey]);

        const handleUpdate = handlePreviewUpdate({
          mockDocumentRef,
          sourceDocumentRef,
          backgroundRef,
          nextFieldData,
          updatedKey
        });

        statusController.performUpdate(handleUpdate);
      }
    }
  );

  useEffect(() => {
    const isInvalid = hasInvalidFilenameChars(formController.fieldData.name);
    props.setDisabled("invalidFilename", {
      isDisabled: isInvalid,
      tooltip: isInvalid ? appstr("exportFilenameInvalidChars") : undefined
    });
  }, []);

  useImperativeHandle(props.handleRef, () => ({
    handleExport
  }));

  const handleExport = async () => {
    exportStartedRef.current = true;

    const exporter = new Exporter();
    await exporter.export({
      type: "image",
      fieldData: formController.fieldData,
      mode: props.mode
    });

    return true;
  };

  return (
    <Flex height="100%">
      <Flex flex={2}>
        <PreviewCanvas
          exportStartedRef={exportStartedRef}
          fieldData={formController.fieldData}
          backgroundRef={backgroundRef}
          mockDocumentRef={mockDocumentRef}
          sourceDocumentRef={sourceDocumentRef}
          statusController={statusController}
        />
      </Flex>
      <Flex flex={1}>
        <Tabs
          customClass={cx("ocn-chart-explorer-tabs", "oc-overflow-hidden")}
          customContentClass={cx("oc-overflow-auto", "ocn-list-scrollbar")}
          tabIndex={currentTab}
          onTabChange={setCurrentTab}
          tabs={[
            {
              header: appstr("General"),
              content: <ImageExportGeneral formController={formController} mode={props.mode} />
            }
          ]}
        />
      </Flex>
    </Flex>
  );
};
